/**
 * MKS-410 — ONE queue: internal and client requests together.
 *
 * Both sources live in the same table (`helpdesk_requests`) since child 2/4
 * bridged the portal's intake into it, so there is one list to triage rather
 * than two surfaces that each know about half the work. Every row is marked by
 * source — Internal, or Client with the project it came in on — because the
 * two need different things from a triager and telling them apart at a glance
 * is the whole reason for a marker.
 *
 * FILTERS LIVE IN THE URL. `useSearchParams` is the single source of truth —
 * no `useState` mirror — so a filtered queue is a shareable link, the back
 * button walks the filter history, and a reload lands on the same view. That
 * is MKS-339's deliberate design and this surface keeps it.
 *
 * The pickers (types, projects, customers, people) are fetched ONCE here and
 * passed down, rather than by each expanded triage panel: a queue of 40 rows
 * would otherwise fetch the user list 40 times.
 */
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Card } from '../ui/Card';
import { Pill } from '../ui/Pill';
import { Btn } from '../ui/Btn';
import { RequestQueueFilters } from './RequestQueueFilters';
import { RequestTriagePanel } from './RequestTriagePanel';
import { formatRelative } from './slaFormat';
import { requestStatusTone, requestSourceTone, requestPriorityLabel } from './requestFormat';
import { useAdminHelpdeskRequests, useHelpdeskRequestTypes } from '../../hooks/useHelpdeskRequests';
import { useProjects, useCustomers } from '../../hooks/useProjects';
import { getAdminUsers } from '../../services/api/admin';
import type { HelpdeskRequestFilters } from '../../services/api/helpdeskRequests';

/** Every filter key the URL may carry, so none is read or written ad hoc. */
const FILTER_KEYS = [
  'status',
  'source',
  'typeId',
  'customerId',
  'project',
  'assignee',
  'needsResolutionNote',
] as const;

function filtersFromParams(params: URLSearchParams): HelpdeskRequestFilters {
  const filters: Record<string, string> = {};
  for (const key of FILTER_KEYS) {
    const value = params.get(key);
    if (value !== null && value !== '') filters[key] = value;
  }
  return filters as HelpdeskRequestFilters;
}

export function RequestQueue() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [expandedId, setExpandedId] = useState<number | null>(null);
  // Projects the backend has refused for want of a Triage column, this
  // session. Lifted here so one row's refusal marks the picker on every row —
  // see `RequestTriagePanel`'s header for why this is learned rather than read.
  const [unroutableProjectIds, setUnroutableProjectIds] = useState<ReadonlySet<number>>(
    () => new Set(),
  );

  const filters = useMemo(() => filtersFromParams(searchParams), [searchParams]);

  const { data: requests, isLoading, isError } = useAdminHelpdeskRequests(filters);
  const { data: types = [] } = useHelpdeskRequestTypes();
  const { data: projects = [] } = useProjects();
  const { data: customers = [] } = useCustomers();
  // `/api/admin/users` rather than the inbox's `owners`: a request can be
  // routed to anyone internal, not only to someone who already owns a ticket.
  // Soft-deleted users are excluded — they cannot pick anything up.
  const { data: people = [] } = useQuery({
    queryKey: ['adminUsers', 'helpdeskTriage'],
    queryFn: () => getAdminUsers().then((us) => us.filter((u) => u.deletedAt === null)),
    staleTime: 60_000,
  });

  const markUnroutable = useCallback((projectId: number) => {
    setUnroutableProjectIds((prev) => {
      if (prev.has(projectId)) return prev;
      const next = new Set(prev);
      next.add(projectId);
      return next;
    });
  }, []);

  function handleFilterChange(key: keyof HelpdeskRequestFilters, value: string) {
    const next = new URLSearchParams(searchParams);
    if (value === '') next.delete(key);
    else next.set(key, value);
    // `replace: false` so each filter change is its own history entry.
    setSearchParams(next);
    setExpandedId(null);
  }

  function handleReset() {
    setSearchParams(new URLSearchParams());
    setExpandedId(null);
  }

  const rows = requests ?? [];

  return (
    <>
      <RequestQueueFilters
        filters={filters}
        types={types}
        projects={projects}
        customers={customers}
        people={people}
        onChange={handleFilterChange}
        onReset={handleReset}
      />

      <Card className="overflow-hidden">
        {isLoading && <p className="text-[12.5px] text-ms-text-muted">Loading requests…</p>}

        {isError && (
          <p className="text-[12.5px] text-rose-600">
            Could not load the queue. Refresh to try again.
          </p>
        )}

        {!isLoading && !isError && rows.length === 0 && (
          <p className="text-[12.5px] text-ms-text-muted" data-testid="request-queue-empty">
            No requests match this view.
          </p>
        )}

        {!isLoading && !isError && rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="text-left text-[10.5px] uppercase tracking-wider text-ms-text-muted">
                  <th className="py-2 pr-3 font-semibold">Source</th>
                  <th className="py-2 pr-3 font-semibold">Type</th>
                  <th className="py-2 pr-3 font-semibold">Request</th>
                  <th className="py-2 pr-3 font-semibold">Reporter</th>
                  <th className="py-2 pr-3 font-semibold">Project</th>
                  <th className="py-2 pr-3 font-semibold">Category</th>
                  <th className="py-2 pr-3 font-semibold">Priority</th>
                  <th className="py-2 pr-3 font-semibold">Status</th>
                  <th className="py-2 pr-3 font-semibold">Handled by</th>
                  <th className="py-2 pr-3 font-semibold">Filed</th>
                  <th className="py-2 font-semibold" />
                </tr>
              </thead>
              <tbody>
                {rows.map((request) => {
                  const statusTone = requestStatusTone(request.status);
                  const sourceTone = requestSourceTone(request.source);
                  const expanded = expandedId === request.id;
                  return (
                    // A row and its triage panel are two <tr>s that must stay
                    // siblings inside <tbody>, so the key goes on the Fragment.
                    <Fragment key={request.id}>
                      <tr
                        className="border-t border-ms-border align-middle"
                        data-testid={`request-row-${request.id}`}
                      >
                        <td className="py-2 pr-3">
                          <Pill bg={sourceTone.bg} fg={sourceTone.fg}>
                            {sourceTone.label}
                          </Pill>
                        </td>
                        <td className="py-2 pr-3 text-ms-text-secondary">
                          {request.typeName}
                        </td>
                        <td className="py-2 pr-3 text-ms-text-strong">
                          {request.title}
                          {/* The ISO record is held open by a closed ticket
                              with no note. Said on the row, not only behind a
                              filter, so it cannot be missed. */}
                          {request.needsResolutionNote && (
                            <span className="ml-2 text-[10.5px] font-semibold text-amber-700">
                              needs a resolution note
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-ms-text-secondary">
                          {request.reporter?.name ?? '—'}
                        </td>
                        <td className="py-2 pr-3 text-ms-text-secondary">
                          {request.projectName ?? 'Not yet routed'}
                        </td>
                        <td className="py-2 pr-3 text-ms-text-secondary">
                          {request.categoryName ?? '—'}
                        </td>
                        <td className="py-2 pr-3 text-ms-text-secondary">
                          {requestPriorityLabel(request.priority)}
                        </td>
                        <td className="py-2 pr-3">
                          <Pill bg={statusTone.bg} fg={statusTone.fg}>
                            {statusTone.label}
                          </Pill>
                        </td>
                        <td className="py-2 pr-3 text-ms-text-secondary">
                          {request.assignee?.name ?? 'Unassigned'}
                        </td>
                        <td className="py-2 pr-3 text-ms-text-secondary">
                          {formatRelative(request.createdAt)}
                        </td>
                        <td className="py-2">
                          <Btn
                            variant="secondary"
                            size="sm"
                            aria-expanded={expanded}
                            onClick={() => setExpandedId(expanded ? null : request.id)}
                          >
                            {expanded ? 'Close' : 'Triage'}
                          </Btn>
                        </td>
                      </tr>
                      {expanded && (
                        <tr>
                          <td colSpan={11} className="p-0">
                            <RequestTriagePanel
                              request={request}
                              people={people}
                              unroutableProjectIds={unroutableProjectIds}
                              onProjectUnroutable={markUnroutable}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
